
import { Component, Prop, Vue } from "vue-property-decorator";
import Moment from "moment";
import httpClient from "@/store/httpClient";
import { CustomerArticleDto } from "@/dtos/customerArticleDto";
import { CustomerOfferDto, CustomerOfferPositionDto } from "@/dtos/customerOfferDto";
import { OfferRequestDto } from "@/dtos/offerRequestDto";
import { OfferRequestCallOffInterval } from "@/dtos/offerRequestCallOffInterval";
import { DeliveryType } from "@/dtos/deliveryType";
import { OfferState } from "@/dtos/offerState";
import { OrderTrackingState } from "@/dtos/orderTrackingState";
import { AddressDto } from "@/dtos/addressDto";
import { ErpAddressType } from "@/dtos/erpAddressType";
import AddressesOverview from "@/views/Settings/AddressesOverview.vue";

/**
 * Shows the offer Blexon made for a series request. Reached from the request list and from
 * the mail that goes out when the offer is forwarded to the customer.
 */
@Component({
	components: {
		AddressesOverview,
	},
	filters: {
		formatPrice(value: number): string {
			return (value || 0).toFixed(2);
		},
		formatDate(value: string | null): string {
			return value ? Moment(value).format("DD.MM.YYYY") : "-";
		},
	},
})
export default class OfferForRequest extends Vue {
	/** Id of the series request as it comes from the route. */
	@Prop()
	private requestId!: string;

	private request: OfferRequestDto | null = null;
	private offer: CustomerOfferDto | null = null;
	private isLoading: boolean = false;
	/** The tab names as they appear in the url. */
	private tabNames = ["angebot", "erstmuster"];
	/** Guards the buttons while a call is on its way. */
	private isWorking: boolean = false;
	/** Guards the pdf button while the document is being made. */
	private isLoadingPdf: boolean = false;
	/** The confirmation the customer has to go through before the samples are ordered. */
	private samplesDialog: boolean = false;
	/** The same before the offer itself is accepted, which is the binding order. */
	private acceptDialog: boolean = false;
	/** The confirmation before a running series is cancelled. */
	private cancelSeriesDialog: boolean = false;
	private acceptedAGB: boolean = false;
	/** The picker the checkout uses, for the two addresses of the offer. */
	private addressPickerDialog: boolean = false;
	private addressPickerType: ErpAddressType = ErpAddressType.Delivery;
	private deliveryAddressType: ErpAddressType = ErpAddressType.Delivery;
	private invoiceAddressType: ErpAddressType = ErpAddressType.Invoice;
	/**
	 * Where the order of the samples stands. Null while there is no such order, and that
	 * is also what it stays at for an offer without a first article inspection.
	 */
	private inspectionOrderState: OrderTrackingState | null = null;

	private mounted() {
		this.load();
	}

	/**
	 * Reads the request and its offer.
	 * @param showLoadingState Whether the page is replaced by the loading message while this
	 * runs. False for a refresh after a small change, where blanking the whole offer for a
	 * moment is more unsettling than useful.
	 */
	private async load(showLoadingState: boolean = true) {
		this.isLoading = showLoadingState;
		try {
			const requestResponse = await httpClient().get(
				`Offer/GetOfferRequest?id=${this.requestId}`
			);
			this.request = Object.assign(new OfferRequestDto(), requestResponse.data);
			const offerResponse = await httpClient().get(
				`Offer/GetOfferForRequest?requestId=${this.requestId}`
			);
			this.offer = offerResponse.data || null;
			if (this.offer !== null) {
				// Names and thumbnails come from the store, which only holds what another
				// view happened to load.
				await this.$store.dispatch(
					"loadMissingArticles",
					this.offer.positions.map((p) => p.articleId)
				);
				await this.loadInspectionOrderState();
			}
		} catch {
			this.$store.dispatch("setSnackbarText", "Das Angebot konnte nicht geladen werden.");
		} finally {
			this.isLoading = false;
		}
	}

	/**
	 * Reads how far the order of the samples has got. The offer only carries the id of that
	 * order, the state comes from the orders themselves, which is where it is kept up to
	 * date. Nothing to read without an order, and a failure here must not keep the offer
	 * from being shown: without a state the offer simply cannot be accepted yet.
	 */
	private async loadInspectionOrderState() {
		this.inspectionOrderState = null;
		const orderId = this.inspectionOrderId;
		if (orderId === null) {
			return;
		}
		try {
			const response = await httpClient().get(`Order/GetOrderTracking?orderId=${orderId}`);
			this.inspectionOrderState = response.data === null ? null : response.data.state;
		} catch (e) {
			console.error("Failed to load the order of the first article inspection.", e);
		}
	}

	/**
	 * The tab lives in the url, so the samples can be linked to from a mail or from the
	 * hint on the offer itself, and a reload stays where it was.
	 */
	private get tab(): number {
		const index = this.tabNames.indexOf(this.$route.params.tab);
		return index < 0 ? 0 : index;
	}

	private set tab(index: number) {
		const tab = this.tabNames[index];
		if (this.$route.params.tab === tab) {
			return;
		}
		this.$router.replace({
			name: "OfferForRequest",
			params: { requestId: String(this.requestId), tab },
		});
	}

	private showInspectionTab() {
		this.tab = 1;
	}

	// --------------------------------------------------------------- actions
	private get isAccepted(): boolean {
		return this.offer !== null && this.offer.state === OfferState.Accepted;
	}

	/** Id of the order of the samples. Null while they were never ordered. */
	private get inspectionOrderId(): number | null {
		if (this.offer === null || this.offer.inspection === null) {
			return null;
		}
		const orderId = this.offer.inspection.orderId;
		return orderId === null || orderId === undefined ? null : orderId;
	}

	/**
	 * Whether the samples were ordered. Taken from the order rather than from the state of
	 * the offer: the offer waits for the answer of the customer again once the samples are
	 * through, which looks exactly like an offer whose samples were never ordered.
	 */
	private get samplesOrdered(): boolean {
		return this.inspectionOrderId !== null;
	}

	/**
	 * With samples the offer can only be accepted once they are through. Invoiced is the
	 * mark for that: an order is only invoiced after it was produced and delivered.
	 */
	private get isInspectionSettled(): boolean {
		return (
			this.inspectionOrderState === OrderTrackingState.Fakturiert ||
			this.inspectionOrderState === OrderTrackingState.Paid
		);
	}

	/** With samples the offer can only be accepted once they are through. */
	private get mustOrderSamplesFirst(): boolean {
		return this.hasInspection && !this.isInspectionSettled;
	}

	/** The offer is only ours to accept while it waits for exactly that. */
	private get canAccept(): boolean {
		if (this.offer === null || this.mustOrderSamplesFirst || !this.hasBothAddresses) {
			return false;
		}
		return this.offer.state === OfferState.WaitingForCustomerAnswer;
	}

	/**
	 * No order without both addresses: the invoice needs one and the delivery papers need
	 * one, a collection included.
	 */
	private get hasBothAddresses(): boolean {
		return (
			this.offer !== null &&
			this.offer.deliveryAddress !== null &&
			this.offer.invoiceAddress !== null
		);
	}

	/** Samples are ordered once, so an order that already exists closes this off. */
	private get canOrderSamples(): boolean {
		return (
			this.hasInspection &&
			!this.samplesOrdered &&
			this.hasBothAddresses &&
			this.offer !== null &&
			this.offer.state === OfferState.WaitingForCustomerAnswer
		);
	}

	// ------------------------------------------------------------- addresses
	/**
	 * Both addresses are the customer's to change as long as nothing is ordered. Once the
	 * samples are ordered or the offer is accepted, the addresses are on an order of their
	 * own and changing them here would say something that is no longer true.
	 */
	private get canChangeAddresses(): boolean {
		return (
			this.offer !== null &&
			this.offer.state === OfferState.WaitingForCustomerAnswer &&
			!this.samplesOrdered
		);
	}

	private get deliveryName(): string {
		return this.offer === null || this.offer.deliveryAddress === null
			? ""
			: `${this.offer.deliveryAddress.givenName} ${this.offer.deliveryAddress.surname}`.trim();
	}

	private get invoiceName(): string {
		return this.offer === null || this.offer.invoiceAddress === null
			? ""
			: `${this.offer.invoiceAddress.givenName} ${this.offer.invoiceAddress.surname}`.trim();
	}

	private openAddressPicker(type: ErpAddressType) {
		this.addressPickerType = type;
		this.addressPickerDialog = true;
	}

	/**
	 * Stores the picked address on the offer right away. Only the values are kept, so an
	 * address the customer changes in their settings afterwards does not change this offer.
	 * The dialog closes and the card shows the new address before the call comes back: saving
	 * takes a moment, and a click that seems to do nothing invites a second one. The offer is
	 * read again afterwards, quietly, so what stands on the screen is what was stored.
	 */
	private async addressPicked(address: AddressDto) {
		this.addressPickerDialog = false;
		if (this.offer !== null) {
			if (address.type === ErpAddressType.Delivery) {
				this.offer.deliveryAddress = address;
			} else {
				this.offer.invoiceAddress = address;
			}
		}
		this.isWorking = true;
		try {
			await httpClient().post(
				`Offer/UpdateOfferAddress?requestId=${this.requestId}`,
				address
			);
		} catch {
			this.$store.dispatch("setSnackbarText", "Die Adresse konnte nicht gespeichert werden.");
		} finally {
			this.isWorking = false;
			// Quietly: the offer stays on the screen instead of blanking out for a refresh.
			await this.load(false);
		}
	}

	private openAcceptDialog() {
		this.acceptedAGB = false;
		this.acceptDialog = true;
	}

	private closeAcceptDialog() {
		this.acceptDialog = false;
		this.acceptedAGB = false;
	}

	/**
	 * Accepting is the binding order, so it is only sent from the dialog. The offer stays
	 * open to look at afterwards and then says that it was accepted, so the view is read
	 * again rather than navigated away from.
	 */
	private async acceptOffer() {
		await this.callAndReload(
			"Offer/AcceptOffer",
			"Das Angebot konnte nicht angenommen werden."
		);
		this.closeAcceptDialog();
	}

	// ----------------------------------------------- what accepting commits to
	/** Days until the invoice of a delivery is due. */
	private get paymentDays(): number {
		return this.offer === null || this.offer.conditions === null
			? 0
			: this.offer.conditions.paymentDays;
	}

	/** What is due before production starts. Zero means nothing is. */
	private get downPayment(): number {
		return this.offer === null || this.offer.conditions === null
			? 0
			: this.offer.conditions.downPayment;
	}

	/** How long the first delivery takes, as "20 Arbeitstage" or "1 Arbeitstag". */
	private get completionWorkingDays(): string {
		const days = this.completionDays;
		return `${days} ${days === 1 ? "Arbeitstag" : "Arbeitstage"}`;
	}

	/** The same in the dative, for a sentence that says "in 20 Arbeitstagen". */
	private get completionWorkingDaysDative(): string {
		const days = this.completionDays;
		return `${days} ${days === 1 ? "Arbeitstag" : "Arbeitstagen"}`;
	}

	private get completionDays(): number {
		return this.offer === null || this.offer.conditions === null
			? 0
			: this.offer.conditions.firstDeliveryWorkingDays;
	}

	/** A series: the customer calls off the first delivery, the rest follow on their own. */
	private get isRecurringOrder(): boolean {
		return (
			this.request !== null &&
			this.request.callOffInterval !== OfferRequestCallOffInterval.Once
		);
	}

	private get isSeriesCancelled(): boolean {
		return this.offer !== null && this.offer.cancelledAt !== null;
	}

	/** Only an accepted series that still runs can be cancelled. */
	private get canCancelSeries(): boolean {
		return this.isAccepted && this.isRecurringOrder && !this.isSeriesCancelled;
	}

	/**
	 * No further call-off is ordered afterwards. The view is read again, so it says that
	 * the series is cancelled.
	 */
	private async cancelSeries() {
		await this.callAndReload("Offer/CancelSeries", "Die Serie konnte nicht gekündigt werden.");
		this.cancelSeriesDialog = false;
	}

	/** Zero call-offs means the series runs until the customer revokes it. */
	private get hasLimitedCallOffs(): boolean {
		return this.request !== null && this.request.callOffCount > 0;
	}

	/** Whether the offer says anything about getting out of a series. */
	private get hasCancellationTerms(): boolean {
		if (this.offer === null || this.offer.conditions === null) {
			return false;
		}
		return (
			this.offer.conditions.cancellationNoticeDays > 0 ||
			this.offer.conditions.minimumCallOffCount > 0
		);
	}

	/** Cancelling now falls short of the call-offs the customer committed to. */
	private get cancellingCausesExtraCosts(): boolean {
		if (this.offer === null || this.offer.conditions === null) {
			return false;
		}
		return this.offer.placedCallOffs < this.offer.conditions.minimumCallOffCount;
	}

	private openSamplesDialog() {
		this.acceptedAGB = false;
		this.samplesDialog = true;
	}

	private closeSamplesDialog() {
		this.samplesDialog = false;
		this.acceptedAGB = false;
	}

	/**
	 * Ordering takes a while, so the dialog stays open with the button spinning until the
	 * order is through. Afterwards there is nothing left to decide here and the customer goes
	 * back to the list, where the request now says it waits for the samples.
	 */
	private async confirmOrderSamples() {
		this.isWorking = true;
		try {
			await httpClient().post(`Offer/OrderInspectionParts?requestId=${this.requestId}`);
			this.closeSamplesDialog();
			this.backToRequests();
		} catch {
			this.$store.dispatch(
				"setSnackbarText",
				"Die Erstmuster konnten nicht bestellt werden."
			);
		} finally {
			this.isWorking = false;
		}
	}

	private navigateToAgb() {
		window.open("https://blexon.com/agb/", "_blank");
	}

	/** How long the samples take, as "5 Arbeitstage" or "1 Arbeitstag". */
	private get inspectionWorkingDays(): string {
		const days = this.inspectionDays;
		return `${days} ${days === 1 ? "Arbeitstag" : "Arbeitstage"}`;
	}

	/** The same in the dative, for a sentence that says "in 5 Arbeitstagen". */
	private get inspectionWorkingDaysDative(): string {
		const days = this.inspectionDays;
		return `${days} ${days === 1 ? "Arbeitstag" : "Arbeitstagen"}`;
	}

	private get inspectionDays(): number {
		return this.offer === null || this.offer.inspection === null
			? 0
			: this.offer.inspection.deliveryWorkingDays;
	}

	/** The state decides what the buttons do, so the offer is read again afterwards. */
	private async callAndReload(action: string, errorText: string) {
		this.isWorking = true;
		try {
			await httpClient().post(`${action}?requestId=${this.requestId}`);
			await this.load();
		} catch {
			this.$store.dispatch("setSnackbarText", errorText);
		} finally {
			this.isWorking = false;
		}
	}

	private backToRequests() {
		this.$router.push({ name: "OfferRequests" });
	}

	/**
	 * The offer as a document, with the samples and the series as tables of their own. It is
	 * for the customer's files and to pass on internally: accepting still happens here.
	 */
	private async downloadPdf() {
		if (this.offer === null) {
			return;
		}
		this.isLoadingPdf = true;
		try {
			const response = await httpClient().get(
				`Offer/GetOfferForRequestAsPdf?requestId=${this.requestId}`,
				{ responseType: "blob" }
			);
			const url = URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement("a");
			link.href = url;
			link.download = `Angebot ${this.offer.offerId}.pdf`;
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			this.$store.dispatch("setSnackbarText", "Das PDF konnte nicht erstellt werden.");
		} finally {
			this.isLoadingPdf = false;
		}
	}

	// ------------------------------------------------------------ conditions
	/**
	 * The conditions as label and value, so the template stays a plain list. Only what is
	 * actually settled shows up: a condition left at zero says nothing.
	 */
	private get conditions(): Array<{ label: string; value: string }> {
		if (this.offer === null || this.offer.conditions === null) {
			return [];
		}
		const conditions = this.offer.conditions;
		const rows: Array<{ label: string; value: string }> = [];
		rows.push({ label: "Bedarf", value: this.callOffText });
		rows.push({
			label: "Erste Fertigstellung",
			value: `frühestens ${conditions.firstDeliveryWorkingDays} Arbeitstage ${
				this.hasInspection ? "nach Freigabe der Erstmuster" : "nach Auftragserteilung"
			}`,
		});
		if (conditions.pricesFixedUntil) {
			rows.push({
				label: "Preise fixiert bis",
				value: Moment(conditions.pricesFixedUntil).format("DD.MM.YYYY"),
			});
		}
		if (conditions.cancellationNoticeDays > 0) {
			rows.push({
				label: "Kündigungsfrist",
				value: `${conditions.cancellationNoticeDays} Tage vor dem nächsten Abruf`,
			});
		}
		if (conditions.minimumCallOffCount > 0) {
			rows.push({
				label: "Mindestanzahl Abrufe",
				value: `${conditions.minimumCallOffCount} Abrufe, bei früherer Kündigung entstehen Zusatzkosten`,
			});
		}
		rows.push({
			label: "Zahlungsfrist",
			value: `${conditions.paymentDays} Tage nach Rechnungsdatum`,
		});
		if (conditions.downPayment > 0) {
			rows.push({
				label: "Anzahlung",
				value: `${this.amount(conditions.downPayment)} vor Produktionsbeginn`,
			});
		}
		return rows;
	}

	/**
	 * How the parts travel, as a position name. The server sends the kind rather than a
	 * name so that it reads in the language of the signed in user.
	 */
	private transportName(kind: DeliveryType): string {
		switch (kind) {
			case DeliveryType.Post:
				return "Verpackung & Versand per Post";
			case DeliveryType.LKW:
				return "Verpackung & Versand per LKW";
			case DeliveryType.Kurier:
				return "Verpackung & Versand per Kurier";
			default:
				return "Verpackung & Versand nach Aufwand";
		}
	}

	/** An amount with its currency, for the places that stand apart from the price columns. */
	private amount(value: number): string {
		const currency = this.offer === null ? "" : this.offer.currency;
		return `${value.toFixed(2)} ${currency}`.trim();
	}

	private get hasInspection(): boolean {
		return this.offer !== null && this.offer.inspection !== null;
	}

	/**
	 * What is credited once the definitive order is placed. Zero when nothing is. Worked out by
	 * the ERP, which prints the same amount on the pdf.
	 */
	private get inspectionCreditAmount(): number {
		if (this.offer === null || this.offer.inspection === null) {
			return 0;
		}
		return this.offer.inspection.creditAmount;
	}

	/** What the samples cost in the end, once the order follows. */
	private get inspectionEffectiveCost(): number {
		return this.offer === null || this.offer.inspection === null
			? 0
			: this.offer.inspection.total - this.inspectionCreditAmount;
	}

	/** What the customer is granted for the samples, in the same shape as the conditions. */
	private get inspectionConditions(): Array<{ label: string; value: string }> {
		if (this.offer === null || this.offer.inspection === null) {
			return [];
		}
		const inspection = this.offer.inspection;
		const rows = [
			{
				label: "Fertigstellung",
				value: `${inspection.deliveryWorkingDays} Arbeitstage nach Auftragserteilung`,
			},
		];
		if (inspection.creditPercentage > 0) {
			rows.push({
				label: "Gutschrift",
				value:
					inspection.creditCallOffs > 1
						? `${inspection.creditPercentage} % über die nächsten ${inspection.creditCallOffs} Abrufe`
						: `${inspection.creditPercentage} % mit der definitiven Bestellung`,
			});
		} else {
			rows.push({ label: "Gutschrift", value: "Keine" });
		}
		rows.push({
			label: "Zahlungsfrist",
			value: `${inspection.paymentDays} Tage nach Rechnungsdatum`,
		});
		if (inspection.downPayment > 0) {
			rows.push({
				label: "Anzahlung",
				value: `${this.amount(inspection.downPayment)} vor Produktionsbeginn`,
			});
		}
		return rows;
	}

	/** The rhythm and how many deliveries, in the words the request was made in. */
	private get callOffText(): string {
		if (this.request === null) {
			return "-";
		}
		if (this.request.callOffInterval === OfferRequestCallOffInterval.Once) {
			return "Einmalige Bestellung";
		}
		const names: { [interval: number]: { every: string; several: string } } = {
			[OfferRequestCallOffInterval.Weekly]: { every: "wöchentlich", several: "Wochen" },
			[OfferRequestCallOffInterval.Monthly]: { every: "monatlich", several: "Monate" },
			[OfferRequestCallOffInterval.Yearly]: { every: "jährlich", several: "Jahre" },
		};
		const name = names[this.request.callOffInterval];
		const count = this.request.callOffIntervalCount;
		const rhythm = count > 1 ? `alle ${count} ${name.several}` : name.every;
		const deliveries =
			this.request.callOffCount > 0
				? `${this.request.callOffCount} Abrufe`
				: "bis auf Widerruf";
		return `${rhythm}, ${deliveries}`;
	}

	// ------------------------------------------------------------- positions
	/**
	 * Packing and shipping of one delivery. Part of every total below: the transport is a
	 * position of the order and is invoiced with it, so a total without it would not be
	 * what the customer pays. Zero when the parts are collected or no manufacturer is
	 * assigned yet, and the same for the list price as for the offer price, because the
	 * manufacturer has no say over it.
	 */
	private get transportPrice(): number {
		return this.offer === null || this.offer.transport === null
			? 0
			: this.offer.transport.price;
	}

	/**
	 * What the articles would cost ordered on their own, at their scale prices, transport
	 * included. The sum of the list prices shown above it, and the same figure the order
	 * carries as its position prices before the order discount is taken off.
	 */
	private get totalOrderPrice(): number {
		const positions =
			this.offer === null
				? 0
				: this.offer.positions.reduce((sum, p) => sum + p.orderPrice, 0);
		return positions + this.transportPrice;
	}

	/**
	 * A single order has one delivery, so there is no call-off to relate the total to. With a
	 * special discount the line is a subtotal: the discount follows below it and is only
	 * taken off on the invoice.
	 */
	private get totalLabel(): string {
		if (this.discountFromFirstDelivery) {
			return "Zwischentotal";
		}
		return this.request !== null &&
			this.request.callOffInterval === OfferRequestCallOffInterval.Once
			? "Total"
			: "Total pro Abruf";
	}

	// ------------------------------------------------------------- discount
	private get discountPercentage(): number {
		return this.offer === null || this.offer.conditions === null
			? 0
			: this.offer.conditions.discountPercentage;
	}

	private get discountFromDelivery(): number {
		return this.offer === null || this.offer.conditions === null
			? 1
			: Math.max(1, this.offer.conditions.discountFromDelivery);
	}

	/** A discount that is there from the start belongs into the total itself. */
	private get discountFromFirstDelivery(): boolean {
		return this.discountPercentage > 0 && this.discountFromDelivery <= 1;
	}

	/** A discount that starts later is shown under the total, as what it will cost then. */
	private get discountFromLaterDelivery(): boolean {
		return this.discountPercentage > 0 && this.discountFromDelivery > 1;
	}

	/**
	 * What the special discount of the manufacturer takes off. Worked out on the positions
	 * alone, because the discount does not touch the transport.
	 */
	private get discountAmount(): number {
		return this.offer === null ? 0 : this.positionsOfferPrice - this.offer.totalWithDiscount;
	}

	/**
	 * What one delivery is worth, packing and shipping included and before any special
	 * discount. This is the figure the order carries, to the rappen, and it is the sum of
	 * the positions shown above it. A special discount of the manufacturer is not in it:
	 * that one is taken off when the invoice is written, not in the price of the order.
	 */
	private get totalOfOrder(): number {
		return this.totalOfferPrice;
	}

	private get totalOfferPrice(): number {
		return this.positionsOfferPrice + this.transportPrice;
	}

	/** The positions alone, which is what the discount is worked out on. */
	private get positionsOfferPrice(): number {
		return this.offer === null
			? 0
			: this.offer.positions.reduce((sum, p) => sum + p.offerPrice, 0);
	}

	/** What a delivery the discount applies to costs, transport included. */
	private get totalWithDiscountToPay(): number {
		return this.offer === null ? 0 : this.offer.totalWithDiscount + this.transportPrice;
	}

	/**
	 * How the total relates to packing and shipping, for the confirmation. Shipping by
	 * effort is not in the total: it is only known once the parts are actually sent, so the
	 * customer must not be told the amount covers it.
	 */
	private get transportNote(): string {
		if (this.offer === null || this.offer.transport === null) {
			return "";
		}
		return this.offer.transport.kind === DeliveryType.NachAufwand
			? "zuzüglich Verpackung und Versand nach Aufwand"
			: "inklusive Verpackung und Versand";
	}

	private get parentOfArticle(): { [articleId: string]: number } {
		const parents: { [articleId: string]: number } = {};
		if (this.offer !== null) {
			this.offer.positions.forEach((p) => {
				(p.includedSubarticleIds || []).forEach((id) => (parents[`${id}`] = p.articleId));
			});
		}
		return parents;
	}

	private isSubarticle(position: CustomerOfferPositionDto): boolean {
		return this.parentOfArticle[`${position.articleId}`] !== undefined;
	}

	/** Same numbering as everywhere else: a subarticle carries the number of its assembly. */
	private positionNumber(position: CustomerOfferPositionDto): string {
		const numbers: { [articleId: string]: string } = {};
		if (this.offer === null) {
			return "";
		}
		this.offer.positions.forEach((p, index) => {
			const parent = this.parentOfArticle[`${p.articleId}`];
			numbers[`${p.articleId}`] =
				parent === undefined ? `${index + 1}` : `${numbers[`${parent}`]}.${index + 1}`;
		});
		return numbers[`${position.articleId}`];
	}

	// -------------------------------------------------------------- articles
	private get allArticles(): CustomerArticleDto[] {
		return this.$store.state.articles as CustomerArticleDto[];
	}

	private article(articleId: number): CustomerArticleDto | undefined {
		return this.allArticles.find((a: CustomerArticleDto) => a.articleId === articleId);
	}

	private articleName(articleId: number): string {
		const article = this.article(articleId);
		if (article === undefined) {
			return `Artikel ${articleId}`;
		}
		return article.customerArticleName || article.customerArticleId;
	}

	/** Material and size, the specification a customer recognises the part by. */
	private articleMeta(articleId: number): string {
		const article = this.article(articleId);
		if (article === undefined) {
			return "";
		}
		const parts = [`Artikel ${articleId}`];
		if (article.materialname) {
			parts.push(article.materialname);
		}
		if (article.length > 0) {
			parts.push(
				`${article.length.toFixed(0)} x ${article.width.toFixed(0)} x ${article.height.toFixed(
					0
				)} mm`
			);
		}
		if (article.weight > 0) {
			parts.push(`${article.weight.toFixed(2)} kg`);
		}
		return parts.join(" · ");
	}

	// The article's lastChange is passed on purpose: without it the url carries the current
	// time and every re-render requests the image again.
	private thumbnailUrl(articleId: number): string {
		const article = this.article(articleId);
		return this.$store.getters.getThumbnailUrl(
			articleId,
			article ? article.lastChange : undefined
		);
	}
}
